/**
 * Sistem centralizat de logging pentru erori
 * Toate erorile din aplicatie sunt trimise aici pentru procesare
 */

import { buildApiUrl } from '@config/api';
import { formatTimeOnly } from './dateFormatter';

export interface ErrorLog {
  timestamp: string;
  type: 'runtime' | 'promise' | 'react' | 'network' | 'manual';
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  componentStack?: string;
  url?: string;
  userAgent?: string;
}

class ErrorLogger {
  private errors: ErrorLog[] = [];
  private maxErrors = 100; // Pastreaza ultimele 100 de erori

  /**
   * Logheaza o eroare si o salveaza in memorie
   */
  log(error: ErrorLog) {
    // Adauga informatii suplimentare
    const enrichedError = {
      ...error,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Adauga in lista de erori
    this.errors.push(enrichedError);

    // Pastreaza doar ultimele maxErrors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Trimite eroarea catre server pentru persistenta
    this.sendToServer(enrichedError);
  }

  /**
   * Returneaza toate erorile logheate
   */
  getErrors(): ErrorLog[] {
    return [...this.errors];
  }

  /**
   * Returneaza erorile grupate pe tip
   */
  getErrorsByType(): Record<string, ErrorLog[]> {
    return this.errors.reduce((acc, error) => {
      if (!acc[error.type]) {
        acc[error.type] = [];
      }
      acc[error.type].push(error);
      return acc;
    }, {} as Record<string, ErrorLog[]>);
  }

  /**
   * Sterge toate erorile din memorie
   */
  clear() {
    this.errors = [];
      }

  /**
   * Exporta erorile ca JSON
   */
  exportAsJSON(): string {
    return JSON.stringify(this.errors, null, 2);
  }

  /**
   * Returneaza un raport sumar al erorilor logate
   */
  printSummary() {
    const byType = this.getErrorsByType();

    return {
      total: this.errors.length,
      generatedAt: formatTimeOnly(new Date().toISOString()),
      byType: Object.fromEntries(
        Object.entries(byType).map(([type, errors]) => [type, errors.length])
      ),
      help: {
        getErrors: 'window.errorLogger.getErrors()',
        exportAsJSON: 'window.errorLogger.exportAsJSON()',
      },
    };
  }

  /**
   * Trimite erorile catre un server (optional)
   */
  private async sendToServer(error: ErrorLog) {
    try {
      // Obtine token-ul de autentificare
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      const userId = user ? JSON.parse(user).id : null;

      await fetch(buildApiUrl('/errors/client'), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          ...error,
          userId,
          environment: 'frontend'
        })
      });
    } catch (err) {
      // Nu logam eroare pentru a nu crea loop infinit
      void err;
    }
  }
}

// Creeaza o instanta singleton
export const errorLogger = new ErrorLogger();

// Expune in window pentru acces din consola
if (typeof window !== 'undefined') {
  (window as any).errorLogger = errorLogger;
}

